import { useState } from "react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { IS_PROD } from "@/configs";
import { Check, Copy, X } from "lucide-react";
import { cn } from "@/lib/utils";

const formatBuildDate = (isoString: string): { date: string; time: string } => {
  const d = new Date(isoString);
  const date = `${d.getUTCFullYear()}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.${String(d.getUTCDate()).padStart(2, "0")}`;
  const time = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  return { date, time };
};

export const VersionBadge = () => {
  const { isAdmin } = useIsAdmin();
  const [copied, setCopied] = useState(false);
  const [minimized, setMinimized] = useState(false);

  if (!isAdmin) return null;

  const buildTimestamp = __BUILD_TIMESTAMP__;
  const { date, time } = formatBuildDate(buildTimestamp);
  const env = IS_PROD ? "PROD" : "STAGING";

  const handleCopy = async () => {
    const info = `Build: v${date} ${time} UTC\nEnv: ${env}\nTimestamp: ${buildTimestamp}`;
    await navigator.clipboard.writeText(info);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-24 left-4 z-50 h-3 w-3 rounded-full bg-primary/60 hover:bg-primary transition-colors"
        title="Show version info"
      />
    );
  }

  return (
    <div className="fixed bottom-24 left-4 z-50 flex items-center gap-1">
      <button
        onClick={handleCopy}
        className={cn(
          "flex flex-col items-start px-2 py-1 rounded-md text-[10px] font-mono leading-tight transition-all",
          "bg-background/80 backdrop-blur-sm border border-border/50 shadow-sm",
          "hover:bg-background hover:border-border"
        )}
        title="Click to copy build info"
      >
        <div className="flex items-center gap-1.5">
          <span className={cn(
            "px-1 rounded text-[9px] font-semibold",
            IS_PROD ? "bg-green-500/20 text-green-600" : "bg-amber-500/20 text-amber-600"
          )}>
            {env}
          </span>
          <span className="text-muted-foreground">v{date}</span>
          {copied ? (
            <Check className="h-2.5 w-2.5 text-green-500" />
          ) : (
            <Copy className="h-2.5 w-2.5 text-muted-foreground/50" />
          )}
        </div>
        <span className="text-muted-foreground/70">{time} UTC</span>
      </button>
      <button
        onClick={() => setMinimized(true)}
        className="p-0.5 rounded hover:bg-muted/50 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
        title="Minimize"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};
